import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, ArrowUpRight, Menu, X, Globe, Check } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { useSite } from "@/context/SiteContext";
import { useUI } from "@/context/UIContext";
import { BOOKING_URL, trackBookingClick } from "@/lib/booking";
import { BiosphereSeal } from "@/components/BiosphereSeal";
import { Logo } from "./Logo";

const LANG_LABELS = {
  es: "Español", en: "English", ca: "Català", fr: "Français",
  de: "Deutsch", it: "Italiano", zh: "中文", ja: "日本語",
};

const NAV = [
  { key: "rooms", id: "nav-rooms", label: "rooms" },
  { key: "services", id: "nav-services", label: "services" },
  { key: "corporate", id: "nav-corporate", label: "corporate" },
  { key: "events", id: "nav-events", label: "events" },
  { key: "restaurant", id: "nav-restaurant", label: "restaurant" },
  { key: "location", id: "nav-location", label: "location" },
  { key: "biosphere", id: "nav-biosphere", label: "biosphere" },
  { key: "contact", id: "nav-contact", label: "contact" },
];
const NAV_MOBILE = [...NAV.slice(0, 7), { key: "gallery", id: "nav-gallery", label: "gallery" }, { key: "blog", id: "nav-blog", label: "blog" }, NAV[7]];

function LangSwitch({ light }) {
  const { lang, langs, altPath } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const trigger = light ? "text-bone/80 hover:text-bone" : "text-charcoal/70 hover:text-charcoal";

  return (
    <div className="relative" ref={ref} data-testid="lang-switch">
      <button
        type="button"
        data-testid="lang-switch-trigger"
        aria-label="Idioma"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${trigger}`}
      >
        <Globe size={14} strokeWidth={1.5} />
        {lang}
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul
            data-testid="lang-switch-menu"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className="absolute right-0 mt-3 w-40 overflow-hidden rounded-sm border border-line bg-sand/95 backdrop-blur-xl shadow-[0_12px_40px_rgba(28,27,26,0.14)] z-50 py-1"
          >
            {langs.map((l) => (
              <li key={l}>
                <Link
                  to={l === lang ? "#" : altPath(l)}
                  onClick={(e) => { if (l === lang) e.preventDefault(); setOpen(false); }}
                  data-testid={`lang-${l}`}
                  aria-current={l === lang ? "true" : undefined}
                  className={`flex items-center justify-between gap-3 px-4 py-2.5 text-sm transition-colors duration-200 ${l === lang ? "text-charcoal font-medium bg-charcoal/[0.04]" : "text-charcoal/70 hover:text-charcoal hover:bg-charcoal/[0.04]"}`}
                >
                  <span className="flex items-center gap-2.5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-charcoal/40 w-5">{l}</span>
                    {LANG_LABELS[l] || l}
                  </span>
                  {l === lang && <Check size={13} strokeWidth={2} className="text-gold" />}
                </Link>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Header() {
  const { t, path, lang } = useLang();
  const { settings } = useSite();
  const { heroDark, menuOpen: open, setMenuOpen: setOpen } = useUI();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.documentElement.classList.toggle("lenis-stopped", open);
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  const light = heroDark && !scrolled && !open;
  const shell = light
    ? "bg-transparent border-transparent"
    : "bg-sand/85 backdrop-blur-xl border-line shadow-[0_1px_0_rgba(28,27,26,0.04)]";

  return (
    <>
      <header data-testid="site-header" className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-500 ${shell}`}>
        <div className="mx-auto flex h-[72px] md:h-20 max-w-site items-center justify-between px-5 sm:px-8 lg:px-12">
          <Logo light={light} />

          <nav className="hidden xl:flex items-center gap-7" aria-label="Principal">
            {NAV.map((n) => (
              <NavLink
                key={n.key}
                to={path(n.key)}
                id={n.id}
                data-testid={n.id}
                className={({ isActive }) =>
                  `link-line whitespace-nowrap font-sans text-[13px] tracking-[0.02em] transition-colors duration-300 ${light ? "text-bone/85 hover:text-bone" : "text-charcoal/75 hover:text-charcoal"} ${isActive ? "!opacity-100" : ""}`
                }
              >
                {t.nav[n.label]}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-5 md:gap-7">
            <a href={`tel:${settings.phone_href || "+34937745160"}`} data-testid="header-phone" className={`hidden 2xl:inline-flex items-center gap-2 whitespace-nowrap font-mono text-[11px] tracking-[0.12em] transition-colors duration-300 ${light ? "text-bone/80 hover:text-bone" : "text-charcoal/70 hover:text-charcoal"}`}>
              <Phone size={13} strokeWidth={1.5} />
              {settings.phone || "+34 937 745 160"}
            </a>
            <LangSwitch light={light} />
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-book-button" onClick={() => trackBookingClick("nav-book-button", lang)} className={`hidden md:inline-flex btn ${light ? "btn-light" : "btn-primary"} !py-3 !px-5`}>
              {t.nav.book}
              <ArrowUpRight size={13} strokeWidth={1.5} />
            </a>
            <button
              type="button"
              data-testid="menu-toggle"
              aria-label={open ? t.nav.close : t.nav.menu}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className={`xl:hidden inline-flex h-10 w-10 items-center justify-center transition-colors duration-300 ${light ? "text-bone" : "text-charcoal"}`}
            >
              {open ? <X size={22} strokeWidth={1.25} /> : <Menu size={22} strokeWidth={1.25} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 bg-ink text-bone flex flex-col pt-20 px-6 sm:px-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <nav className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-1 py-2" aria-label="Menú móvil">
              {NAV_MOBILE.map((n, i) => (
                <motion.div key={n.key} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.05 + i * 0.04, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
                  <NavLink to={path(n.key)} data-testid={`mobile-${n.id}`} className="group flex items-baseline justify-between border-b border-white/10 py-3">
                    <span className="font-serif text-2xl sm:text-3xl">{t.nav[n.label]}</span>
                    <span className="font-mono text-[10px] tracking-[0.2em] text-gold" aria-hidden="true">0{i + 1}</span>
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }} className="shrink-0 border-t border-white/10 pt-5 pb-8 flex flex-col gap-4">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-menu-book" onClick={() => trackBookingClick("mobile-menu-book", lang)} className="btn btn-light w-full">
                {t.nav.book} <ArrowUpRight size={13} />
              </a>
              <a href={`tel:${settings.phone_href || "+34937745160"}`} className="font-mono text-xs tracking-[0.15em] text-bone/70 flex items-center gap-2">
                <Phone size={13} /> {settings.phone || "+34 937 745 160"}
              </a>
              <BiosphereSeal onDark />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
